import api from '../api';
import { ColorPalette } from '../utils/colorPaletteGenerator';

export interface ClientBranding {
  id?: number;
  project_id?: number;
  company_name: string;
  logo: string | null;
  logo_filename?: string | null;
  color_palette: ColorPalette;
  primary_color: string;
  secondary_color?: string;
  accent_color?: string;
  created_by?: number;
  updated_by?: number;
  created_at?: string;
  updated_at?: string;
}

export interface SaveBrandingRequest {
  companyName: string;
  logo?: string | null;
  logoFilename?: string | null;
  colorPalette: ColorPalette;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
}

class ClientBrandingAPI {
  /**
   * Save or update branding for a project
   */
  async saveBranding(projectId: number, branding: SaveBrandingRequest): Promise<{
    success: boolean;
    data?: ClientBranding;
    message?: string;
  }> {
    try {
      const response = await api.post(`/api/projects/${projectId}/branding`, branding);
      return response.data;
    } catch (error: any) {
      console.error('Error saving branding:', error);
      throw new Error(error.response?.data?.message || 'Failed to save branding');
    }
  }

  /**
   * Get branding for a project
   */
  async getBranding(projectId: number, includeDefault: boolean = false): Promise<{
    success: boolean;
    data?: ClientBranding | null;
    message?: string;
  }> {
    try {
      const response = await api.get(`/api/projects/${projectId}/branding`, {
        params: { includeDefault }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching branding:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch branding');
    }
  }

  /**
   * Update branding for a project
   */
  async updateBranding(projectId: number, branding: SaveBrandingRequest): Promise<{
    success: boolean;
    data?: ClientBranding;
    message?: string;
  }> {
    try {
      const response = await api.put(`/api/projects/${projectId}/branding`, branding);
      return response.data;
    } catch (error: any) {
      console.error('Error updating branding:', error);
      throw new Error(error.response?.data?.message || 'Failed to update branding');
    }
  }

  /**
   * Delete branding for a project
   */
  async deleteBranding(projectId: number): Promise<{
    success: boolean;
    message?: string;
  }> {
    try {
      const response = await api.delete(`/api/projects/${projectId}/branding`);
      return response.data;
    } catch (error: any) {
      console.error('Error deleting branding:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete branding');
    }
  }

  /**
   * Get default branding (fallback)
   */
  async getDefaultBranding(): Promise<{
    success: boolean;
    data: ClientBranding;
  }> {
    try {
      const response = await api.get('/api/branding/default');
      return response.data;
    } catch (error: any) {
      console.error('Error fetching default branding:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch default branding');
    }
  }

  /**
   * Validate color palette structure
   */
  async validateColorPalette(colorPalette: ColorPalette): Promise<{
    success: boolean;
    data: {
      valid: boolean;
      missingColors?: string[];
      invalidColors?: string[];
    };
  }> {
    try {
      const response = await api.post('/api/branding/validate-palette', { colorPalette });
      return response.data;
    } catch (error: any) {
      console.error('Error validating palette:', error);
      throw new Error(error.response?.data?.message || 'Failed to validate palette');
    }
  }

  /**
   * Get branding or default (convenience method)
   */
  async getBrandingOrDefault(projectId: number): Promise<ClientBranding> {
    try {
      const result = await this.getBranding(projectId, true);
      return result.data!;
    } catch (error) {
      // If error, fetch default branding
      const defaultResult = await this.getDefaultBranding();
      return defaultResult.data;
    }
  }
}

export default new ClientBrandingAPI();
